import { isForbiddenError, isSuccess } from '@/context/services/type';
import { useGetHeaderWithOfficeId } from '@/features/ip_address_whitelists/hooks/useGetHeaderWithOfficeId';
import { useQueryClient } from '@tanstack/react-query';
import {
  CommonError,
  getGetIpAddressRestrictionSettingQueryKey,
  useGetIpAddressRestrictionSettingSuspense,
  useUpdateIpAddressRestrictionSetting,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';

export const useGetIpAddressRestrictionSetting = () => {
  const { data, error } = useGetIpAddressRestrictionSettingSuspense();
  if (error) throw error;
  return useMemo(
    () => ({
      data: data?.data,
    }),
    [data]
  );
};

type TUpdateIpAddressRestrictionSettingMutateArgs = ReturnType<
  typeof useUpdateIpAddressRestrictionSetting
>['mutateAsync'];
type UpdateIpAddressRestrictionSettingMutateArgs =
  Parameters<TUpdateIpAddressRestrictionSettingMutateArgs>['0'];

export const useUpdateIpAddressRestrictionSettingService = (
  officeId: string
) => {
  const query = useQueryClient();
  const headerWithOfficeId = useGetHeaderWithOfficeId(officeId);
  const { mutateAsync: _mutateAsync } =
    useUpdateIpAddressRestrictionSetting(headerWithOfficeId);
  const mutateAsync = useCallback(
    async (args: UpdateIpAddressRestrictionSettingMutateArgs) => {
      const resp = await _mutateAsync(args);
      if (isSuccess(resp.status)) {
        await query.invalidateQueries({
          queryKey: getGetIpAddressRestrictionSettingQueryKey(),
        });
        return {
          status: 'success' as const,
          data: resp.data,
        };
      } else if (isForbiddenError(resp.status)) {
        return {
          status: 'forbidden' as const,
          redirect_url: resp?.headers?.location ?? undefined,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: resp.data as CommonError,
        };
      }
      return {
        status: 'error' as const,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        errors: (resp.data ?? {}) as CommonError,
      };
    },
    [_mutateAsync, query]
  );
  return { mutateAsync };
};
